import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Trash2, Search, Ruler } from 'lucide-react';
import toast from 'react-hot-toast';
import { sizeService } from '../../services';

const SizeMaster = () => {
  const [sizes, setSizes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingSize, setEditingSize] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  // Load sizes from API
  useEffect(() => {
    loadSizes();
  }, []);

  const loadSizes = async () => {
    try {
      setLoading(true);
      const response = await sizeService.getAll({ search: searchTerm });
      setSizes(response.data || []);
    } catch (error) {
      console.error('Error loading sizes:', error);
      toast.error('Failed to load sizes');
    } finally {
      setLoading(false);
    }
  };

  // Reload sizes when search term changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadSizes();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const openModal = (size = null) => {
    setEditingSize(size);
    setShowModal(true);
    if (size) {
      reset({
        name: size.name,
        code: size.code,
        is_active: size.is_active
      });
    } else {
      reset({ name: '', code: '', is_active: true });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSize(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (editingSize) {
        // Update size
        await sizeService.update(editingSize.id, data);
        toast.success('Size updated successfully!');
      } else {
        // Create new size
        await sizeService.create(data);
        toast.success('Size created successfully!');
      }
      closeModal();
      loadSizes(); // Reload the list
    } catch (error) {
      console.error('Error saving size:', error);
      toast.error('Failed to save size');
    } finally {
      setLoading(false);
    }
  };

  const deleteSize = async (id) => {
    if (window.confirm('Are you sure you want to delete this size?')) {
      try {
        setLoading(true);
        await sizeService.delete(id);
        toast.success('Size deleted successfully!');
        loadSizes(); // Reload the list
      } catch (error) {
        console.error('Error deleting size:', error);
        toast.error('Failed to delete size');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleStatus = async (size) => {
    try {
      setLoading(true);
      await sizeService.update(size.id, { 
        name: size.name,
        code: size.code,
        is_active: !size.is_active 
      });
      toast.success('Size status updated!');
      loadSizes(); // Reload the list
    } catch (error) {
      console.error('Error updating size status:', error);
      toast.error('Failed to update size status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>
            Size Master
          </h1>
          <button
            onClick={() => openModal()}
            className="btn btn-primary"
          >
            <Plus size={18} />
            Add Size
          </button>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '20px', position: 'relative', maxWidth: '300px' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6b7280'
            }}
          />
          <input
            type="text"
            placeholder="Search sizes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-box"
            style={{ paddingLeft: '40px' }}
          />
        </div>

        {/* Sizes Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Size Name</th>
                <th>Code</th>
                <th>Status</th>
                <th>Created Date</th>
                <th>Updated Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sizes.map((size) => (
                <tr key={size.id}>
                  <td>{size.id}</td>
                  <td style={{ fontWeight: '500' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Ruler size={16} color="#6b7280" />
                      {size.name}
                    </div>
                  </td>
                  <td>{size.code || '-'}</td>
                  <td>
                    <button
                      onClick={() => toggleStatus(size)}
                      className={`status-badge ${size.is_active ? 'status-active' : 'status-inactive'}`}
                      style={{ cursor: 'pointer', border: 'none' }}
                      disabled={loading}
                    >
                      {size.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>{new Date(size.created_at).toLocaleDateString()}</td>
                  <td>{new Date(size.updated_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => openModal(size)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 8px', fontSize: '12px' }}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => deleteSize(size.id)}
                        className="btn btn-danger"
                        style={{ padding: '6px 8px', fontSize: '12px' }}
                        disabled={loading}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            Loading sizes...
          </div>
        )}

        {!loading && sizes.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            No sizes found.
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingSize ? 'Edit Size' : 'Add New Size'}
              </h2>
              <button onClick={closeModal} className="close-btn">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label className="form-label">Size Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter size name (e.g., Small, Medium, Large)"
                  {...register('name', {
                    required: 'Size name is required',
                    minLength: {
                      value: 1,
                      message: 'Size name must be at least 1 character'
                    }
                  })}
                />
                {errors.name && (
                  <div className="error-message">{errors.name.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Size Code (Optional)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter size code (e.g., S, M, L, XL)"
                  {...register('code')}
                />
                {errors.code && (
                  <div className="error-message">{errors.code.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  {...register('is_active', { required: 'Status is required' })}
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
                {errors.is_active && (
                  <div className="error-message">{errors.is_active.message}</div>
                )}
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingSize ? 'Update Size' : 'Create Size'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SizeMaster;
